import {
  CURRENCY_BY_MARKET,
  SEED_MARKETS,
  marketsForScope,
  type BalanceLedgerEntry,
  type ChargebackReasonCode,
  type Currency,
  type DisputeSource,
  type DisputeStatus,
  type Market,
  type Transaction,
  type TransactionStatus,
  type User,
} from '../dataProvider/types';

/**
 * The Chargebacks query/aggregation module (D-chargebacks) - the ONE place disputed
 * transactions are shaped for the worklist. Same seam idiom as the manual-attribution
 * queue (a virtual list over transactions): v1 aggregates client-side from the mock
 * provider, every selector lives here, and the late real-data migration swaps this
 * module for a server-side endpoint without the surface changing.
 *
 * A dispute is `disputeStatus !== 'none'` on a transaction, ORTHOGONAL to settlement
 * `status` (a disputed transaction stays `confirmed`). The worklist is organised by
 * the dispute lifecycle: received (needs attention), under_review (in progress), and
 * resolved (won or lost, history). Each row carries the disputed transaction plus the
 * member's TRUE SIGNED balance (the admin truth, which can be negative after a
 * clawback) and the reversed amount when lost.
 *
 * REGION: this surface is OPERATIONAL and SWITCHER-SCOPED, NOT investigative-global
 * (D-chargebacks, the deliberate divergence from the fraud-class-is-global rule). A
 * chargeback is a cashout-shaped thing (one transaction, one member, one market), so
 * it follows the access scope via marketsForScope, exactly like the cashout queue. A
 * single-region reviewer sees only their market's disputes.
 */

export interface DisputeRow {
  /** The disputed transaction's id (the row id). */
  id: number;
  userId: number;
  memberName: string;
  market: Market;
  currency: Currency;
  grossAmount: number;
  cashbackAmount: number;
  /** Settlement status, which STAYS confirmed through the dispute. */
  status: TransactionStatus;
  disputeStatus: DisputeStatus;
  disputeSource: DisputeSource | null;
  disputeReasonCode: ChargebackReasonCode | null;
  disputeNote: string | null;
  resolvedAt: string | null;
  occurredAt: string;
  /** The member's true SIGNED balance (the admin figure, can be negative). */
  memberBalanceSigned: number;
  /** Total reversed for this transaction (a lost dispute), else 0. */
  reversalAmount: number;
}

export interface ChargebacksView {
  received: DisputeRow[];
  underReview: DisputeRow[];
  resolved: DisputeRow[];
}

export interface ChargebacksInput {
  transactions: Transaction[];
  users: User[];
  /** The shared signed balance ledger; only chargeback_reversal entries are read. */
  ledger: BalanceLedgerEntry[];
}

export interface ChargebacksOptions {
  /** Access scope (UK | US | GLOBAL) from the region switcher. */
  scope: string;
}

/** The markets a scope renders, kept to the operating markets (the cashouts pattern). */
const marketsFor = (scope: string): Market[] =>
  marketsForScope(scope).filter((m) => SEED_MARKETS.includes(m));

const isDisputed = (t: Transaction): boolean =>
  t.disputeStatus != null && t.disputeStatus !== 'none';

const oldestFirst = (a: DisputeRow, b: DisputeRow): number =>
  a.occurredAt.localeCompare(b.occurredAt) || a.id - b.id;

const resolvedFirst = (a: DisputeRow, b: DisputeRow): number =>
  (b.resolvedAt ?? '').localeCompare(a.resolvedAt ?? '') || a.id - b.id;

export function buildChargebacksView(
  input: ChargebacksInput,
  opts: ChargebacksOptions,
): ChargebacksView {
  const markets = marketsFor(opts.scope);
  const usersById = new Map(input.users.map((u) => [u.id, u]));
  // The lost-dispute clawback is a NEGATIVE ledger entry; sum its MAGNITUDE per
  // transaction so the row shows a positive "clawed back" figure. Manual adjustments
  // share the ledger but carry no transactionId and are skipped here.
  const reversalByTxn = new Map<number, number>();
  for (const e of input.ledger) {
    if (e.kind !== 'chargeback_reversal' || e.transactionId == null) continue;
    reversalByTxn.set(
      e.transactionId,
      (reversalByTxn.get(e.transactionId) ?? 0) + Math.abs(Number(e.amount)),
    );
  }

  const rows: DisputeRow[] = input.transactions
    .filter(isDisputed)
    .filter((t) => t.userId != null && markets.includes(t.market))
    .map((t) => {
      const user = usersById.get(t.userId as number);
      return {
        id: t.id,
        userId: t.userId as number,
        memberName: user?.name ?? '',
        market: t.market,
        currency: CURRENCY_BY_MARKET[t.market],
        grossAmount: t.grossAmount,
        cashbackAmount: t.cashbackAmount,
        status: t.status,
        disputeStatus: t.disputeStatus as DisputeStatus,
        disputeSource: t.disputeSource ?? null,
        disputeReasonCode: t.disputeReasonCode ?? null,
        disputeNote: t.disputeNote ?? null,
        resolvedAt: t.resolvedAt ?? null,
        occurredAt: t.occurredAt,
        // The admin sees the TRUE signed balance (can be negative after a clawback),
        // not the member-facing floored one.
        memberBalanceSigned: Number(user?.balanceSigned ?? 0),
        reversalAmount: reversalByTxn.get(t.id) ?? 0,
      };
    });

  return {
    received: rows
      .filter((r) => r.disputeStatus === 'received')
      .sort(oldestFirst),
    underReview: rows
      .filter((r) => r.disputeStatus === 'under_review')
      .sort(oldestFirst),
    resolved: rows
      .filter((r) => r.disputeStatus === 'won' || r.disputeStatus === 'lost')
      .sort(resolvedFirst),
  };
}
